import axios from 'axios';
import { ChatMessage } from '../../types';
import { API_CONFIG } from '../../config/api';

// URL base do backend (remove /api/pareceres da webhook URL)
const BACKEND_URL = API_CONFIG.WEBHOOK_URL.replace('/api/pareceres', '');

// Modelo padrão
export const DEFAULT_MODEL_ID = 'qwen/qwen3-235b-a22b';

// Modelos disponíveis pré-definidos para fallback
export const AVAILABLE_MODELS = [
    { id: 'qwen/qwen3-235b-a22b', name: 'Qwen3 235B A22B', provider: 'Qwen', contextWindow: 40960 },
    { id: 'google/gemini-2.5-pro-preview-06-05', name: 'Gemini 2.5 Pro Preview', provider: 'Google', contextWindow: 1000000 },
    { id: 'openai/gpt-4.1', name: 'GPT-4.1', provider: 'OpenAI', contextWindow: 1047576 },
    { id: 'anthropic/claude-sonnet-4', name: 'Claude Sonnet 4', provider: 'Anthropic', contextWindow: 200000 },
];

export interface OpenRouterModel {
    id: string;
    name: string;
    description?: string;
    context_length: number;
    created?: number;
    pricing: {
        prompt: string;
        completion: string;
    };
}

/**
 * Ordena modelos por data de criação (mais recentes primeiro)
 */
export function sortModelsByCreated(models: OpenRouterModel[]): OpenRouterModel[] {
    return [...models].sort((a, b) => {
        const aCreated = a.created || 0;
        const bCreated = b.created || 0;
        return bCreated - aCreated;
    });
}

/**
 * Busca modelos disponíveis do OpenRouter via backend
 */
export async function fetchOpenRouterModels(): Promise<OpenRouterModel[]> {
    try {
        const response = await axios.get(`${BACKEND_URL}/api/openrouter/models`, {
            headers: {
                'X-API-Token': API_CONFIG.TOKEN,
            },
        });

        if (response.data && Array.isArray(response.data)) {
            // Backend já ordena por data de criação, mas garantimos a ordenação aqui também
            return sortModelsByCreated(response.data);
        }
        return [];
    } catch (error) {
        console.error('Falha ao buscar modelos OpenRouter:', error);
        return [];
    }
}

/**
 * Envia mensagem para o OpenRouter via backend (sem streaming)
 */
export async function sendMessageToOpenRouter(
    model: string,
    messages: ChatMessage[]
): Promise<string> {
    if (!API_CONFIG.TOKEN) {
        throw new Error('Token da API não configurado. Verifique a variável VITE_API_TOKEN.');
    }

    try {
        const formattedMessages = messages.map(msg => ({
            role: msg.role,
            content: msg.content,
        }));

        const response = await axios.post(
            `${BACKEND_URL}/api/openrouter/send`,
            {
                model,
                messages: formattedMessages,
            },
            {
                headers: {
                    'Content-Type': 'application/json',
                    'X-API-Token': API_CONFIG.TOKEN,
                },
            }
        );

        if (response.data?.content) {
            return response.data.content;
        }

        throw new Error('Resposta inválida do servidor');
    } catch (error: any) {
        console.error('Erro ao chamar OpenRouter:', error);

        if (error.response) {
            console.error('Resposta de erro:', JSON.stringify(error.response.data, null, 2));
            const errorMessage = error.response?.data?.error?.message || error.message || 'Falha ao enviar mensagem';
            throw new Error(`Erro da API: ${errorMessage}`);
        }

        throw new Error(error.message || 'Falha ao enviar mensagem');
    }
}

/**
 * Envia mensagem para o OpenRouter via backend com streaming SSE
 * @returns AsyncGenerator que emite chunks de texto conforme são recebidos
 */
export async function* streamMessageFromBackend(
    model: string,
    messages: ChatMessage[]
): AsyncGenerator<string> {
    if (!API_CONFIG.TOKEN) {
        throw new Error('Token da API não configurado. Verifique a variável VITE_API_TOKEN.');
    }

    const formattedMessages = messages.map(msg => ({
        role: msg.role,
        content: msg.content,
    }));

    const response = await fetch(`${BACKEND_URL}/api/openrouter/chat`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-API-Token': API_CONFIG.TOKEN,
        },
        body: JSON.stringify({ model, messages: formattedMessages }),
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Erro HTTP ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
        throw new Error('Stream não disponível');
    }

    const decoder = new TextDecoder();

    try {
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value, { stream: true });

            // Parse SSE data - cada linha começa com "data: "
            const lines = chunk.split('\n');
            for (const line of lines) {
                if (line.startsWith('data: ')) {
                    const data = line.slice(6);
                    if (data === '[DONE]') {
                        return;
                    }
                    try {
                        const parsed = JSON.parse(data);
                        const content = parsed.choices?.[0]?.delta?.content;
                        if (content) {
                            yield content;
                        }
                    } catch {
                        // Ignora linhas que não são JSON válido
                    }
                }
            }
        }
    } finally {
        reader.releaseLock();
    }
}
